import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { ApiCallError, callFunction } from '@/lib/api';
import { AuthShell } from './AuthShell';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

interface AcceptInvitationOutput {
  user_id: string;
  email: string;
  clinic_id: string;
  role: string;
  session: {
    access_token: string;
    refresh_token: string;
  };
}

export function AcceptInvitation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <AuthShell
        title={t('auth.accept_invitation.title')}
        subtitle={t('auth.accept_invitation.subtitle')}
        footer={
          <Link to="/sign-in" className="font-medium text-indigo-600 hover:text-indigo-500">
            {t('auth.forgot_password.back_to_sign_in')}
          </Link>
        }
      >
        <ErrorAlert message={t('auth.accept_invitation.missing_token')} />
      </AuthShell>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (password.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const result = await callFunction<
        { token: string; password: string },
        AcceptInvitationOutput
      >('accept-invitation', { token, password });

      const { error: setErr } = await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });
      if (setErr) throw setErr;

      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiCallError) {
        switch (err.code) {
          case 'not-found':
            setError(t('auth.accept_invitation.invalid_or_expired'));
            break;
          case 'unauthorized':
            setError(t('auth.accept_invitation.existing_user_password'));
            break;
          case 'invalid-input':
            setError(t('auth.accept_invitation.bad_input'));
            break;
          default:
            setError(err.message || t('auth.errors.unknown'));
        }
      } else {
        setError(err instanceof Error ? err.message : t('auth.errors.unknown'));
      }
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.accept_invitation.title')}
      subtitle={t('auth.accept_invitation.subtitle')}
      footer={
        <Link to="/sign-in" className="font-medium text-indigo-600 hover:text-indigo-500">
          {t('auth.forgot_password.back_to_sign_in')}
        </Link>
      }
    >
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label={t('auth.accept_invitation.password')}
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={t('auth.accept_invitation.password_hint')}
        />
        <Input
          label={t('auth.accept_invitation.password_confirm')}
          name="passwordConfirm"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('auth.accept_invitation.submitting') : t('auth.accept_invitation.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
